/* ==========================================================================
   cart-addon.js — 购物车"顺手买一件"卡片区交互
   用途: 购物车页 cart-addon-item.liquid 渲染的加购推荐卡片
         （添加 / 改量 / 换规格 / Show More-Less 折叠）
   依赖: b2g.js（bgg-stepper 能力源，全局注入）、cart-component.js（T4SThemeSP）、
         theme ajax cart（Shopify.actions.updateCart 已配置 eventTarget=_items）
   机制: 全部使用 document 级事件委托 + CafeAsync 原语，购物车刷新重建 DOM
         后无需重新绑定（只读 data-cart-addon-* 属性与块内 config JSON）
   ========================================================================== */

(function () {
  'use strict';

  if (window.__cartAddonInit) return;
  window.__cartAddonInit = true;

  const NS = 'cart-addon';

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function getCurrencyRate() {
    const r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return r && isFinite(r) ? r : 1;
  }

  function formatMoneyCents(cents) {
    const moneyFormat = window.__pdp__config__?.moneyFormat;
    const fmt = window.T4SThemeSP?.Currency?.formatMoney;
    if (fmt) return fmt(cents, moneyFormat) || '';
    // T4SThemeSP 尚未就绪 —— 退化为小数
    return (cents / 100).toFixed(2);
  }

  function getCartToken() {
    const m = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    if (!m) return null;
    return decodeURIComponent(m[1]).split('?')[0];
  }

  // 通过 SFAPI 查询购物车每行的 { id, viewKey }，建立 viewKey → CartLine GID 映射
  async function resolveCartLineIds() {
    const token = getCartToken();
    if (!token) return null;
    const cartId = 'gid://shopify/Cart/' + token;
    try {
      const res = await fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query:
            'query GetCartLines($cartId: ID!) { cart(id: $cartId) { lines(first: 250) { nodes { id viewKey } } } }',
          variables: { cartId: cartId },
        }),
      }).then(function (r) {
        return r.json();
      });
      const nodes = (res && res.data && res.data.cart && res.data.cart.lines.nodes) || [];
      const map = new Map();
      nodes.forEach(function (n) {
        if (n.viewKey) map.set(n.viewKey, n.id);
      });
      return map;
    } catch (e) {
      console.error('[' + NS + '] SFAPI cart query failed', e);
      return null;
    }
  }

  function readGiftConfig(block) {
    const el = block ? block.querySelector('[data-cart-addon-config]') : null;
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return null;
    }
  }

  function variantGid(id) {
    return 'gid://shopify/ProductVariant/' + id;
  }

  function addonAttributes(cfg, giftId) {
    return [
      { key: '_b2g_addon', value: 'true' },
      { key: '_b2g_activity_id', value: String(cfg.activityId) },
      { key: '_b2g_gift_id', value: String(giftId) },
    ];
  }

  // ── 加购：黑底 + 按钮 ───────────────────────────────────────────────────
  async function handleAdd(btn) {
    if (btn.dataset.loading === 'true') return;
    if (typeof Shopify === 'undefined' || !Shopify.actions || !Shopify.actions.updateCart) {
      console.warn('[' + NS + '] Shopify.actions.updateCart unavailable');
      return;
    }
    const card = btn.closest('[data-cart-addon-card]');
    const block = card ? card.closest('[data-cart-addon]') : null;
    if (!card || !block) return;

    const cfg = readGiftConfig(block);
    if (!cfg) return;

    const giftId = card.dataset.giftId;
    const variantId = card.dataset.variantId;
    const perQty = parseInt(card.dataset.perQty, 10) || 1;
    if (!giftId || !variantId) return;
    if (!(cfg.gifts || []).some(function (g) { return String(g.giftId) === String(giftId); })) return;

    const lineIds = await resolveCartLineIds();
    const parentLineId = lineIds ? lineIds.get(cfg.parentViewKey) : null;
    if (!lineIds || !parentLineId) {
      console.warn('[' + NS + '] Could not resolve parent CartLine GID for', cfg.parentViewKey);
      return;
    }

    btn.dataset.loading = 'true';
    btn.setAttribute('aria-busy', 'true');
    try {
      await Shopify.actions.updateCart({
        lines: [
          {
            quantity: perQty,
            merchandiseId: variantGid(variantId),
            parent: { lineId: parentLineId },
            attributes: addonAttributes(cfg, giftId),
          },
        ],
      });
      saveOpenStates();
      if (window.T4SThemeSP && T4SThemeSP.Cart && T4SThemeSP.Cart.getToFetch) {
        T4SThemeSP.Cart.getToFetch();
      }
    } catch (err) {
      console.error('[' + NS + '] add failed', err);
    } finally {
      delete btn.dataset.loading;
      btn.removeAttribute('aria-busy');
    }
  }

  // ── 改量：已加购数量 stepper ────────────────────────────────────────────

  // mini cart 标题栏 ×N 角标：块内所有卡片 lineQty 之和（购物车刷新重建后
  // Liquid 已重算，这里仅做乐观更新后的即时反馈）
  function refreshBadges(block) {
    if (!block) return;
    const badge = block.querySelector('[data-cart-addon-badge]');
    if (!badge) return;
    let total = 0;
    qsa('[data-cart-addon-card]', block).forEach(function (card) {
      const s = card.querySelector('bgg-stepper');
      if (s) total += parseInt(s.dataset.value, 10) || 0;
    });
    if (total > 0) {
      badge.textContent = '× ' + total;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }

  // ── 删除：stepper dec 到最小值时移除购物车行 ──────────────────────────
  async function removeLine(stepper) {
    const card = stepper.closest('[data-cart-addon-card]');
    const block = card ? card.closest('[data-cart-addon]') : null;
    if (!card) return;
    const lineKey = card.dataset.lineKey;
    if (!lineKey) return;
    if (typeof Shopify === 'undefined' || !Shopify.actions || !Shopify.actions.updateCart) return;

    const lineIds = await resolveCartLineIds();
    const lineId = lineIds ? lineIds.get(lineKey) : null;
    if (!lineIds || !lineId) {
      console.warn('[' + NS + '] Could not resolve addon CartLine GID for', lineKey);
      return;
    }

    refreshBadges(block);
    try {
      await Shopify.actions.updateCart({ lines: [{ id: lineId, quantity: 0 }] });
      saveOpenStates();
      if (window.T4SThemeSP && T4SThemeSP.Cart && T4SThemeSP.Cart.getToFetch) {
        T4SThemeSP.Cart.getToFetch();
      }
    } catch (err) {
      console.error('[' + NS + '] remove failed', err);
    }
  }

  async function updateLineQty(stepper, nextValue) {
    const card = stepper.closest('[data-cart-addon-card]');
    const block = card ? card.closest('[data-cart-addon]') : null;
    if (!card) return;
    const lineKey = card.dataset.lineKey;
    if (!lineKey) return;
    if (typeof Shopify === 'undefined' || !Shopify.actions || !Shopify.actions.updateCart) {
      console.warn('[' + NS + '] Shopify.actions.updateCart unavailable');
      return;
    }

    const lineIds = await resolveCartLineIds();
    const lineId = lineIds ? lineIds.get(lineKey) : null;
    if (!lineIds || !lineId) {
      console.warn('[' + NS + '] Could not resolve addon CartLine GID for', lineKey);
      return;
    }

    const prev = parseInt(stepper.dataset.value, 10) || 1;
    stepper.dataset.value = String(nextValue); // 乐观更新（observedAttribute → 重渲染）
    refreshBadges(block);
    try {
      await Shopify.actions.updateCart({
        lines: [{ id: lineId, quantity: nextValue }],
      });
      saveOpenStates();
      if (window.T4SThemeSP && T4SThemeSP.Cart && T4SThemeSP.Cart.getToFetch) {
        T4SThemeSP.Cart.getToFetch();
      }
    } catch (err) {
      console.error('[' + NS + '] qty update failed', err);
      stepper.dataset.value = String(prev);
      refreshBadges(block);
    }
  }

  // ── 换规格：bgg-variant-modal（PDP 分支）→ b2g:variant-selected ─────────
  let pendingSwitch = null; // { giftId, lineKey, lineQty, card }

  function onBeforeFetch(e) {
    const trigger = e.target;
    if (!trigger || typeof trigger.closest !== 'function') return;
    const card = trigger.closest('[data-cart-addon-card]');
    if (!card) return;

    const stepperEl = card.querySelector('bgg-stepper');
    const lineQty = stepperEl ? parseInt(stepperEl.dataset.value, 10) : null;
    pendingSwitch = {
      giftId: card.dataset.giftId,
      lineKey: card.dataset.lineKey || '',
      lineQty: lineQty || parseInt(card.dataset.perQty, 10) || 1,
      card: card,
    };

    // 将当前行数量同步到 data-params，保证服务端渲染 qty 正确
    try {
      const params = new URLSearchParams(trigger.getAttribute('data-params') || '');
      params.set('qty', String(pendingSwitch.lineQty));
      params.set('variant', String(card.dataset.variantId));
      trigger.setAttribute('data-params', params.toString());
    } catch (_) {}
  }

  function onModalConfirm() {
    if (!pendingSwitch) return;
    const overlay = document.getElementById('switch-specs-modal-overlay');
    const body = overlay ? overlay.querySelector('.switch-specs__body') : null;
    const switcher = body ? body.querySelector('variant-switcher') : null;
    const variant = switcher && switcher.currentVariant;
    if (!variant) return;

    const p = pendingSwitch;
    if (p.lineKey) {
      // 已在购物车 → 更新行 merchandiseId
      doLineVariantUpdate(p, variant.id);
    } else {
      // 未加购 → 更新卡片 data-variant-id 后直接触发加购
      applyVariantToCard(p.giftId, variant);
      const addBtn = p.card ? p.card.querySelector('[data-cart-addon-add]') : null;
      if (addBtn) handleAdd(addBtn);
    }
    if (window.DsmDialog) window.DsmDialog.close('switch-specs-modal');
    pendingSwitch = null;
  }

  async function doLineVariantUpdate(p, variantId) {
    if (typeof Shopify === 'undefined' || !Shopify.actions || !Shopify.actions.updateCart) {
      console.warn('[' + NS + '] Shopify.actions.updateCart unavailable');
      return;
    }
    const lineIds = await resolveCartLineIds();
    const lineId = lineIds ? lineIds.get(p.lineKey) : null;
    if (!lineIds || !lineId) {
      console.warn('[' + NS + '] Could not resolve addon CartLine GID for', p.lineKey);
      return;
    }
    try {
      await Shopify.actions.updateCart({
        lines: [{ id: lineId, merchandiseId: variantGid(variantId), quantity: p.lineQty }],
      });
      saveOpenStates();
      if (window.T4SThemeSP && T4SThemeSP.Cart && T4SThemeSP.Cart.getToFetch) {
        T4SThemeSP.Cart.getToFetch();
      }
    } catch (err) {
      console.error('[' + NS + '] variant update failed', err);
    }
  }

  function applyVariantToCard(giftId, variant) {
    qsa('[data-cart-addon-card][data-gift-id="' + CSS.escape(String(giftId)) + '"]').forEach(
      function (card) {
        card.dataset.variantId = String(variant.id);
        const label = card.querySelector('[data-cart-addon-variant-label]');
        if (label && variant.title && variant.title !== 'Default Title') {
          label.textContent = variant.title;
        }
      }
    );
  }

  // ── 展开状态持久化：加购/改量触发 getToFetch 重建 DOM 后恢复展开 ──────────
  // 用 activityId 标识 block，DOM 重建后重新查找并移除 is--collapsed
  const _openActivityIds = new Set();

  function saveOpenStates() {
    qsa('[data-cart-addon]').forEach(function (block) {
      const cards = block.querySelector('[data-cart-addon-cards]');
      if (cards && !cards.classList.contains('is--collapsed')) {
        const actId = block.dataset.activityId;
        if (actId) _openActivityIds.add(actId);
      }
    });
  }

  function restoreOpenStates() {
    if (!_openActivityIds.size) return;
    qsa('[data-cart-addon]').forEach(function (block) {
      const actId = block.dataset.activityId;
      if (!actId || !_openActivityIds.has(actId)) return;
      const cards = block.querySelector('[data-cart-addon-cards]');
      const toggleBtn = block.querySelector('[data-cart-addon-toggle]');
      const textEl = toggleBtn ? toggleBtn.querySelector('[data-cart-addon-toggle-text]') : null;
      if (cards) cards.classList.remove('is--collapsed');
      if (toggleBtn) toggleBtn.classList.add('is--open');
      if (textEl && toggleBtn && toggleBtn.dataset.showLess) {
        textEl.textContent = toggleBtn.dataset.showLess;
      }
    });
  }

  document.addEventListener('cart:updated', function () {
    restoreOpenStates();
  });
  function toggleExpand(btn) {
    const block = btn.closest('[data-cart-addon]');
    if (!block) return;
    const cards = block.querySelector('[data-cart-addon-cards]');
    const textEl = btn.querySelector('[data-cart-addon-toggle-text]');
    const isOpen = btn.classList.contains('is--open');

    if (!isOpen) {
      btn.classList.add('is--open');
      btn.setAttribute('aria-expanded', 'true');
      if (cards) cards.classList.remove('is--collapsed');
      if (textEl && btn.dataset.showLess) textEl.textContent = btn.dataset.showLess;
    } else {
      btn.classList.remove('is--open');
      btn.setAttribute('aria-expanded', 'false');
      if (cards) cards.classList.add('is--collapsed');
      if (textEl && btn.dataset.showMore) textEl.textContent = btn.dataset.showMore;
    }
  }

  // ── document 级事件委托（购物车刷新重建 DOM 后依然有效） ────────────────
  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('[data-cart-addon-add]') : null;
    if (btn) {
      e.preventDefault();
      handleAdd(btn);
      return;
    }
    const toggle = e.target && e.target.closest ? e.target.closest('[data-cart-addon-toggle]') : null;
    if (toggle) {
      e.preventDefault();
      toggleExpand(toggle);
    }
  });

  document.addEventListener('bgg-stepper:dec', function (e) {
    console.log('bgg-stepper:dec', e.target);
    const stepper = e.target;
    if (!(stepper instanceof HTMLElement) || !stepper.closest('[data-cart-addon]')) return;
    const cur = parseInt(stepper.dataset.value, 10) || 1;
    const step = parseInt(stepper.dataset.step, 10) || 1;
    if (cur - step >= 1) {
      updateLineQty(stepper, cur - step);
    } else {
      // 数量已是 1，点减号 = 删除行，卡片回到「+」按钮状态
      removeLine(stepper);
    }
  });

  document.addEventListener('bgg-stepper:inc', function (e) {
    const stepper = e.target;
    if (!(stepper instanceof HTMLElement) || !stepper.closest('[data-cart-addon]')) return;
    const max = parseInt(stepper.dataset.max, 10) || 9999;
    const cur = parseInt(stepper.dataset.value, 10) || 1;
    const step = parseInt(stepper.dataset.step, 10) || 1;
    if (cur + step <= max) updateLineQty(stepper, cur + step);
  });

  document.addEventListener('bgg-stepper:set', function (e) {
    const stepper = e.target;
    if (!(stepper instanceof HTMLElement) || !stepper.closest('[data-cart-addon]')) return;
    const value = e.detail && e.detail.value;
    if (!value) return;
    const cur = parseInt(stepper.dataset.value, 10) || 0;
    if (value !== cur) updateLineQty(stepper, value);
  });

  document.addEventListener('section-modal:before-fetch', onBeforeFetch);

  const overlay = document.getElementById('switch-specs-modal-overlay');
  if (overlay) {
    overlay.addEventListener('dsm:dialog:confirm', onModalConfirm);
  } else {
    document.addEventListener('dsm:dialog:confirm', function (e) {
      if (e.target && e.target.id === 'switch-specs-modal-overlay') onModalConfirm();
    });
  }

  /**
   * AMOUNT_OFF 活动：将购物车页卡片的 sale-price 用汇率换算后写入。
   * Liquid 直出用 USD 元直接减，错误；JS 在 cart:updated 后修正。
   */
  function renderAmountOffPrices() {
    const rate = getCurrencyRate();
    qsa('[data-cart-addon-sale-price][data-amount-off-usd]').forEach(function (saleEl) {
      const usdAmount = parseFloat(saleEl.getAttribute('data-amount-off-usd'));
      if (!usdAmount || usdAmount <= 0) return;
      const card = saleEl.closest('[data-cart-addon-card]');
      if (!card) return;
      const origEl = card.querySelector('[data-cart-addon-original-price]');
      // original price 分已在 cart-addon-item.liquid 的 _ca_cart_final_prices 中，
      // 已加购时由购物车返回实际分就是正确值；未加购时取 original-price span
      let origCents = origEl ? parseInt(origEl.dataset.priceCents || origEl.textContent.replace(/[^0-9]/g, ''), 10) : NaN;
      // 更可靠：直接从 data-original-price 读（card 上有存）
      const cardOrigCents = parseInt(card.dataset.originalPrice, 10);
      if (!isNaN(cardOrigCents)) origCents = cardOrigCents;
      if (isNaN(origCents) || origCents <= 0) return;
      const offCents = Math.round(usdAmount * rate * 100);
      const saleCents = Math.max(0, origCents - offCents);
      saleEl.textContent = formatMoneyCents(saleCents);
      if (origEl) origEl.style.display = origCents > saleCents ? '' : 'none';
    });
  }

  // cart:updated 时执行（此时购物车 DOM 已渲染完成）
  document.addEventListener('cart:updated', renderAmountOffPrices);
})();
